/**
 * SearchBar - Unified search for users, hashtags, posts, events, and clubs.
 * Locations are intentionally excluded from unified search results.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchAll } from '../../helpers/searchHelpers';
import UserAvatar from '../Profile/UserAvatar';

const EMPTY_RESULTS = { users: [], hashtags: [], posts: [], events: [], clubs: [] };

function SearchBar({ onHashtagSelect, resultsMode = 'dropdown' }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!query.trim()) {
      setResults(null);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await searchAll(query.trim());
        setResults(data);
      } catch {
        setResults(EMPTY_RESULTS);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasResults =
    results &&
    (results.users?.length > 0 ||
      results.hashtags?.length > 0 ||
      results.posts?.length > 0 ||
      results.events?.length > 0 ||
      results.clubs?.length > 0);
  const inlineResults = resultsMode === 'inline';
  const showResults = (inlineResults || open) && (query || hasResults);

  const closeAndClear = () => {
    setQuery('');
    setResults(null);
    setOpen(false);
  };

  const handleHashtagClick = (tag) => {
    onHashtagSelect?.(tag);
    closeAndClear();
  };

  const handleUserClick = (username) => {
    navigate(`/profile/${encodeURIComponent(username)}`);
    closeAndClear();
  };

  const handlePostClick = (postId) => {
    navigate(`/posts/${postId}`);
    closeAndClear();
  };

  const handleEventClick = (eventId) => {
    navigate(`/events/${eventId}`);
    closeAndClear();
  };

  const handleClubClick = (clubId) => {
    navigate(`/clubs/${clubId}`);
    closeAndClear();
  };

  const resultsPanel = showResults ? (
    <div
      className={
        inlineResults
          ? 'mt-3 bg-charcoal rounded-xl border border-shadow shadow-soft overflow-hidden'
          : 'absolute left-0 right-0 top-full mt-1 z-50 bg-charcoal rounded-xl border border-shadow shadow-soft max-h-96 overflow-y-auto'
      }
      data-testid="search-results-panel"
    >
      {loading || (query.trim() && results === null) ? (
        <p className="px-3 py-4 text-silver text-sm">Searching...</p>
      ) : hasResults ? (
        <div className={inlineResults ? 'py-2' : 'py-2'}>
          {results.users?.length > 0 && (
            <div className="px-3 py-1">
              <p className="text-silver text-xs font-medium uppercase">Users</p>
              {results.users.map((u) => (
                <button
                  key={u.user_id}
                  type="button"
                  onClick={() => handleUserClick(u.username)}
                  aria-label={`${u.displayname || u.username} @${u.username}`}
                  className="w-full flex items-center gap-3 text-left px-2 py-2 text-ash hover:bg-shadow rounded-lg text-sm"
                >
                  <UserAvatar
                    user={u}
                    alt=""
                    className="h-9 w-9 flex-shrink-0 rounded-full"
                    emojiClassName="text-sm"
                  />
                  <span className="min-w-0">
                    <span className="block truncate">{u.displayname || u.username}</span>
                    <span className="block truncate text-xs text-silver">@{u.username}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {results.hashtags?.length > 0 && (
            <div className="px-3 py-1">
              <p className="text-silver text-xs font-medium uppercase">Hashtags</p>
              {results.hashtags.map((h) => (
                <button
                  key={h.hashtag_id}
                  type="button"
                  onClick={() => handleHashtagClick(h.hashtag)}
                  className="w-full text-left px-2 py-2 text-ether hover:bg-shadow rounded-lg text-sm"
                >
                  #{h.hashtag}
                </button>
              ))}
            </div>
          )}
          {results.posts?.length > 0 && (
            <div className="px-3 py-1">
              <p className="text-silver text-xs font-medium uppercase">Posts</p>
              {results.posts.map((p) => (
                <button
                  key={p.post_id}
                  type="button"
                  onClick={() => handlePostClick(p.post_id)}
                  className="w-full text-left px-2 py-2 text-ash hover:bg-shadow rounded-lg text-sm"
                >
                  <span className="block truncate">{p.content}</span>
                  {p.poster_username && (
                    <span className="block text-silver text-xs mt-0.5">
                      @{p.poster_username}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {results.events?.length > 0 && (
            <div className="px-3 py-1">
              <p className="text-silver text-xs font-medium uppercase">Events</p>
              {results.events.map((e) => (
                <button
                  key={e.event_id}
                  type="button"
                  onClick={() => handleEventClick(e.event_id)}
                  className="w-full text-left px-2 py-2 text-ash hover:bg-shadow rounded-lg text-sm"
                >
                  <span className="block truncate">{e.name}</span>
                  {e.description && (
                    <span className="block text-silver text-xs mt-0.5 truncate">
                      {e.description}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {results.clubs?.length > 0 && (
            <div className="px-3 py-1">
              <p className="text-silver text-xs font-medium uppercase">Clubs</p>
              {results.clubs.map((c) => (
                <button
                  key={c.club_id}
                  type="button"
                  onClick={() => handleClubClick(c.club_id)}
                  className="w-full text-left px-2 py-2 text-ash hover:bg-shadow rounded-lg text-sm"
                >
                  <span className="block truncate">{c.name}</span>
                  {c.description && (
                    <span className="block text-silver text-xs mt-0.5 truncate">
                      {c.description}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : query.trim() ? (
        <p className="px-3 py-4 text-silver text-sm">No results</p>
      ) : null}
    </div>
  ) : null;

  return (
    <div ref={containerRef} className={inlineResults ? '' : 'relative'}>
      <div
        className={inlineResults ? 'sticky top-0 z-10 bg-void px-4 py-3 border-b border-shadow' : ''}
        data-testid={inlineResults ? 'search-input-sticky' : undefined}
      >
        <input
          type="text"
          placeholder="Search..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="w-full px-3 py-2 rounded-xl bg-charcoal border border-shadow text-ash placeholder-silver text-sm focus:outline-none focus:ring-2 focus:ring-ether/50"
        />
      </div>
      {resultsPanel}
    </div>
  );
}

export default SearchBar;
